import {ChangeDetectionStrategy, Component, inject} from '@angular/core';
import {
  EmergencyProcedureSummarySortingProperty,
  FacetValueResult, FindEmergencyProcedures,
  SortDirection,
  EmergencyProcedureSummary,
  VisitSummaryStatus, GetEmergencyProcedureSummaryWithMutations
} from "@portbase/bezoekschip-service-typescriptmodels";
import {AppContext} from "../../app-context";
import lodash from "lodash";
import {DateFieldRange, MomentDateFieldRange} from "../../common/date/date-range/date-field-range";
import moment from "moment/moment";
import {sendQuery} from "../../common/utils";
import {FacetStats, QuickView} from "../common/facets/facet-filter/facet-filter.component";
import {SentenceCasePipe} from "../../common/sentence-case.pipe";
import {PortvisitUtils} from "../../refdata/portvisit-utils";
import {DataQuery, FacetedOverview} from "../common/overview/faceted-overview";
import {FacetedOverviewFilters} from "../common/overview/overview.component";
import {Handler} from "../../common/handlers/handler";
import {HandleQuery} from "../../common/handlers/handle-query";
import {HandleEvent} from "../../common/handlers/handle-event";
import {Observable, of} from "rxjs";
import {now} from "moment";
import {EmergencyProcedureService} from "../../admin/emergencyprocedure/emergencyprocedure.service";
import {EmergencySummaryExpanded} from "./emergency-overview-item/emergency-overview-item.component";
import {EmergencyOverviewUtils, EmergencySummaryWithDummyfields} from "./emergency-overview.utils";
import {EmergencyProcedureExportService} from "./services/emergency-procedure-export.service";

const quickViewsEmergency: QuickView[] = [{
  code: "INCOMING",
  name: "Incoming",
  icon: "fa-arrow-right-to-bracket",
  facets: {
    harbourMasterStatus: ["INCOMING"]
  }
  }, {
  code: "SHIFTING_OUTGOING",
  name: "Shifting/Outgoing",
  icon: "fa-arrow-right-from-bracket",
  facets: {
    harbourMasterStatus: ["SHIFTING_OUTGOING"]
  }
}];

@Component({
    selector: 'app-emergency-overview',
    templateUrl: './emergency-overview.component.html',
    styleUrls: ['./emergency-overview.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
@Handler()
export class EmergencyOverviewComponent extends FacetedOverview<EmergencySummaryExpanded, EmergencyOverviewFilters> {
  appContext = AppContext;
  sortingProperties: EmergencyProcedureSummarySorting[] = [{
    property: EmergencyProcedureSummarySortingProperty.ARRIVAL_DATE,
    direction: SortDirection.DESC
  }, {
    property: EmergencyProcedureSummarySortingProperty.ARRIVAL_DATE,
    direction: SortDirection.ASC
  }];
  cachedVisits: { [key: string]: EmergencyProcedureSummary[] } = {};
  hiddenFacets: string[] = ["count", "ownerShortName", "declarantShortName", "allowedViewers", "actionRequiredForAgents",
    "actionRequiredForStevedores", "actionRequiredForShipOperator", "activeDeadlinesForStevedores", "hasActiveDeadline", "harbourMasterStatus"]
    .concat(AppContext.showPortCallOptimization() ? [] : ["rtaUnequalEta", "unequalEtc", "unequalMooring", "unequalBollards"])
    .map(h => h.toLowerCase());
  facetOrdering: string[] = ["visitStatus", "portName", "stevedore", "quay", "tidalShip",
    "unreadHarbourRemarks", "overlandedContainers", "shortlandedContainers"];
  otherFacetNames: string[] = ["overlandedContainers", "shortlandedContainers"];
  totalCount: number = 0;

  readonly emergencyProcedureService = inject(EmergencyProcedureService);
  readonly emergencyProcedureExportService = inject(EmergencyProcedureExportService);

  constructor() {
    super("emergency-overview-filters", quickViewsEmergency, quickViewsEmergency[0]);
    this.filters.overviewFilters.selectedQuickView = quickViewsEmergency[0];
  }

  getDataQuery = (term: string): DataQuery<FindEmergencyProcedures> => {
    const timeRange = this.prepareDateTimeRange(this.filters.overviewFilters.filters.timeRange, !term, {
      start: "2023-01-01T00:00:00.000Z",
      end: moment(now()).endOf("date").add(1, "quarter").toISOString()
    });
    const queryFn = this.filters.overviewFilters.selectedQuickView?.queryFn;
    const query = <DataQuery<FindEmergencyProcedures>>{
      className: "com.portbase.bezoekschip.common.api.emergencyprocedure.FindEmergencyProcedures",
      payload: {
        term: term,
        dateTimeRange: timeRange,
        pagination: { from: this.from, count: this.maxItems},
        sortingProperty: this.sortingProperty.property,
        sortDirection: this.sortingProperty.direction,
        ownCallsOnly: this.filters.ownCallsOnly,
        facetFilters: this.getFacetFilters()
      }
    };

    return queryFn ? queryFn(query) : query;
  }

  get sortingProperty(): EmergencyProcedureSummarySorting {
    return this.filters?.sorting || (this.filters?.sortingProperty ? {
      property: this.filters.sortingProperty,
      direction: this.filters.sortDirection
    } : null) || {
      property: EmergencyProcedureSummarySortingProperty.ARRIVAL_DATE,
      direction: SortDirection.DESC
    };
  }

  get visitStatusFilterIncludingFacets(): VisitSummaryStatus[] {
    return this.getMergedFacetFilters()["visitStatus"]?.map(v => v as VisitSummaryStatus) || []
  }

  getHiddenFacets = (): string[] => [];

  changeSorting = (sortingProperty: EmergencyProcedureSummarySorting) => {
    this.filters.sorting = sortingProperty;
    this.filters.sortDirection = this.filters.sortingProperty = null;
    this.saveFiltersInLocalStorage(this.filters);
    this.loadData();
  }

  setOwnsCallsOnly = (ownCallsOnly: boolean) => {
    this.filters.ownCallsOnly = ownCallsOnly;
    this.saveFiltersInLocalStorage(this.filters);
    this.loadData();
  }

  arrivalDateChanged = (date: DateFieldRange) => {
    this.filters.overviewFilters.filters.timeRange = date ? <DateFieldRange>{
      start: moment(date.start).toISOString(),
      end: moment(date.end).toISOString()
    } : null;
    this.data = [];
    this.loadData();
  }

  renderRecords = (result: EmergencySummaryExpanded[], append: boolean) => {
    this.saveFiltersInLocalStorage(this.filters);
    if (!append) {
      this.cachedVisits = {};
    }

    const visits = result.map(v => EmergencyOverviewUtils.toExtendedSummary(v));
    Object.entries(lodash.groupBy(visits, (v) => v.visitStatus))
      .forEach(e => this.cachedVisits[e[0]] = (this.cachedVisits[e[0]] || []).concat(e[1] as EmergencyProcedureSummary))

    if (visits.length < this.maxItems) {
      this.endReached = true;
    }
    this.data = append ? (this.data || []).concat(visits) : visits;
  }

  @HandleEvent("emergencyProcedureMutationsSaved", {global: true})
  onMutationsSaved({crn}: {crn: string}): void {
    sendQuery<EmergencyProcedureSummary>(
      "com.portbase.bezoekschip.common.api.emergencyprocedure.GetEmergencyProcedureSummaryWithMutations",
      <GetEmergencyProcedureSummaryWithMutations>{crn},
      {showSpinner: false, caching: false}
    ).subscribe(summary => {
      const index = this.data.findIndex(v => v.crn === crn);
      if (index >= 0) {
        const updated = EmergencyOverviewUtils.toExtendedSummary(summary as EmergencySummaryWithDummyfields);
        this.data = (this.data || []).map(visit => visit.crn === crn ? updated : visit);
      }
    });
  }

  protected setFacetsFromResult(facets: { [p: string]: FacetValueResult[] }) {
    super.setFacetsFromResult(facets);
    const countFacet = this.allFacets?.find(e => e?.name.toLowerCase() === "count");
    this.totalCount = countFacet ? lodash.sum(countFacet.values.map(e => e.count)) : 0;
  }

  trackByRecord = (index: number, obj: EmergencySummaryExpanded): any => obj.crn;

  nameFormatter = (name: string): string => {
    switch (name) {
      case "unreadHarbourRemarks": return "Harbour master remarks";
      case "rtaUnequalEta": return "ETA-RTA mismatch";
      case "bunkeringLate": return "Bunkering may/will cause delay";
      case "unequalMooring": return "Mooring mismatch";
      case "unequalBollards": return "Bollards mismatch";
      case "unequalEtc": return "ETD-ETC mismatch";
      case "willBeTransferred": return "Handover to other agent";
      case "hasHarbourDues": return "Harbour dues";
      case "harbourDues/status": return "Harbour dues status";
      default: return SentenceCasePipe.formatWithSpaces(name);
    }
  }

  getSortDirectionFormatter(sortDirection: SortDirection): string {
    switch (sortDirection) {
      case SortDirection.ASC: return "ascending";
      case SortDirection.DESC: return "descending";
    }
  }

  static visitStatusFormatter: (value: string) => string = v => {
    if (v === "ARRIVED") return "In Port"
    return PortvisitUtils.enumFormatter(v);
  };

  getValueFormatter = (name: string): (value: string) => string => {
    switch (name) {
      case "visitStatus": return EmergencyOverviewComponent.visitStatusFormatter;
      case "quay": return SentenceCasePipe.format;
      case "orderStatus": case "harbourDues/status": return PortvisitUtils.enumFormatter;
      default: return null;
    }
  }

  getValuesSelectedCallback = (name: string): (facet: FacetStats, selectedValues: string[]) => string[] => {
    switch (name) {
      case "overlandedContainers": case "shortlandedContainers":
        return (facet, selectedValues) => selectedValues.includes("true")
          ? AppContext.isAdmin() ? ["ADMIN"] : [AppContext.userProfile.organisation.shortName]
          : [];
      default: return null;
    }
  }

  protected sortingFormatter = (sortingProperty: EmergencyProcedureSummarySorting): string => {
    switch (sortingProperty.property) {
      case EmergencyProcedureSummarySortingProperty.ARRIVAL_DATE: return `Arrival (${this.getSortDirectionFormatter(sortingProperty.direction)})`;
    }
  }

  filterFacetValues = (name: string, values: FacetValueResult[]): FacetValueResult[] => {
    switch (name) {
      case "overlandedContainers": case "shortlandedContainers":
        const facetValues = values.find(v => AppContext.isAdmin() ? v.value === "ADMIN"
          :  v.value === AppContext.userProfile.organisation.shortName);
        return [{
          value: "true",
          count: facetValues?.count || 0
        }];
      default:
        return values;
    }
  }

  ownCallsFormatter = (ownCallsOnly: boolean) => ownCallsOnly ? "My calls" : "All calls";

  ranges: MomentDateFieldRange[] = [
    {
      label: 'Today',
      start: moment().startOf('day'),
      end: moment().add(1, "day").startOf('day').subtract(1, 'day')
    },
    {
      label: 'Yesterday',
      start: moment().subtract(1, 'day').startOf('day'),
      end: moment().startOf('day').subtract(1, 'day')
    },
    {
      label: 'This week',
      start: moment().startOf('week'),
      end: moment().add(1, "week").startOf('week').subtract(1, 'day')
    },
    {
      label: 'Last 7 days',
      start: moment().subtract(7, 'day').startOf('day'),
      end: moment().add(1, "day").startOf('day').subtract(1, 'day')
    },
    {
      label: 'Next 7 days',
      start: moment().startOf('day'),
      end: moment().add(7, "day").startOf('day').subtract(1, 'day')
    }
  ];

  export(): void {
    this.emergencyProcedureExportService.export();
  }

  @HandleQuery("getFacetValues", { global: true })
  getFacetValues(facetName: string): Observable<FacetValueResult[]> {
    if (Object.keys(this.filters.overviewFilters.facets).length) {
      return super.getFacetValues(facetName);
    }
    return of(this.allFacets.find(f => f.name === facetName).values);
  }
}

interface EmergencyOverviewFilters extends FacetedOverviewFilters<EmergencyOverviewFacetFilters> {
  sortingProperty?: EmergencyProcedureSummarySortingProperty;
  sortDirection?: SortDirection;
  sorting?: EmergencyProcedureSummarySorting;
  ownCallsOnly?: boolean;
  attentionRequired?: boolean;
}

interface EmergencyOverviewFacetFilters {
  timeRange?: DateFieldRange;
}

interface EmergencyProcedureSummarySorting {
  property: EmergencyProcedureSummarySortingProperty;
  direction: SortDirection;
}
